<template>
  <div ref="outerContainerRef">
    <sf-multiselect
      v-model="selectedAccount"
      data-testid="adjustments-account-select"
      name="adjustment-category"
      mode="single"
      :append-to-body="true"
      :can-clear="true"
      :can-select-on-tab="true"
      :clear-on-select="true"
      :close-on-select="true"
      :focused="focused"
      :error="error"
      :loading="isLoading"
      :no-options-text="noOptionsText"
      :object="true"
      :options="accountSelectorInfo?.accounts"
      :placeholder="placeholder"
      :searchable="true"
      track-by="label"
      filter-field="label"
      variant="sf-input"
      @focus="handleFocus"
      @select="handleSelect"
      @search-change="handleSearch"
      @tab-forward="handleTabForward"
      @tab-back="handleTabBack"
    >
      <template #option="{ option }">
        <div class="tw-mx-[-3px]">
          <AdjustmentsAccountItem
            :label="
              `${option.originalNumber ?? ''} ${
                option.originalName ?? ''
              }`.trim()
            "
            :name="`${option.number ?? ''} ${option.name ?? ''}`.trim()"
          ></AdjustmentsAccountItem>
        </div>
      </template>

      <template #beforelist>
        <p class="tw-uppercase tw-font-medium tw-text-xs tw-m-2">
          {{ accountSelectorInfo.accountsText }}
        </p>
      </template>
      <template #afterlist>
        <div v-if="accountSelectorInfo?.newAccounts?.length > 0">
          <div
            class="
              tw-p-2
              tw-text-xs
              tw-font-medium
              tw-uppercase
              tw-border-t-1
              tw-border-b-0
              tw-border-l-0
              tw-border-r-0
              tw-border-gray-200
              tw-border-solid
            "
          >
            {{ accountSelectorInfo?.newAccountsText }}
          </div>
          <div
            v-for="account in accountSelectorInfo?.newAccounts"
            :key="account.number"
            :data-testid="`new-account-${account.number}`"
            class="tw-p-2 tw-text-sm hover:tw-bg-gray-100"
            @click="createNewAccount(account)"
          >
            <AdjustmentsAccountItem
              :name="
                account.name && account.number
                  ? `${account.number} ${account.name}`
                  : account.number
              "
            ></AdjustmentsAccountItem>
          </div>
        </div>
      </template>
    </sf-multiselect>
  </div>
</template>

<script setup lang="ts">
import {
  AccountSelectorInfo,
  SubAdjustmentRowAccount,
  SubAdjustmentRowAccountExisting,
  SubAdjustmentRowAccountNew,
} from '@app/application/adjustments/types'

import { useVModel } from '@vueuse/core'
import debounce from 'lodash/debounce'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref } from 'vue'

import AdjustmentsAccountItem from './adjustments-account-item.vue'
import { fetchAdjustmentAccounts } from '@app/application/adjustments/api'
import { abortAutocompleteReason } from '@app/application/adjustments/constants'
import { abort } from '@app/adapters/abort'
import { SfLog } from '@app/adapters/logger'
import { emptyAccountSelectorInfo } from '@app/application/adjustments/constants'
import SfMultiselect from '@app/shared/vue/components/sf-multiselect.vue'

import { useAdjustmentsModalStore } from '@app/application/adjustments/store/adjustment-modal'
import useI18n from '@app/shared/vue/composables/use-I18n'

interface Props {
  focused?: number
  error?: string
  modelValue: SubAdjustmentRowAccount | null
}

const props = withDefaults(defineProps<Props>(), {
  focused: 0,
  error: '',
  modelValue: () => ({} as SubAdjustmentRowAccount),
})

const outerContainerRef = ref<HTMLElement | null>(null)
const isLoading = ref(false)

const emit =
  defineEmits<{
    selected: []
    'update:modelValue': [value: SubAdjustmentRowAccountExisting]
    'create-new-account': [account: SubAdjustmentRowAccountNew]
    'tab-back': [e: Event]
    'tab-forward': [e: Event]
  }>()

const selectedAccount = useVModel(props, 'modelValue', emit, {
  passive: true,
  deep: true,
})

const modalStore = useAdjustmentsModalStore()

const I18n = useI18n()

const placeholder = I18n.t('adjustment.modal.account_select_placeholder')

const { ledgerId } = storeToRefs(modalStore)

const accountSelectorInfo = ref<AccountSelectorInfo>(emptyAccountSelectorInfo)

const createNewAccount = (account: SubAdjustmentRowAccountNew) => {
  emit('create-new-account', account)
}

const searchText = ref('')

const noOptionsText = computed(() => {
  if (!searchText.value) {
    return placeholder
  }

  return undefined
})

const handleFocus = () => {
  outerContainerRef.value?.scrollIntoView()
}

const handleSelect = (value: SubAdjustmentRowAccount | undefined) => {
  if (value) {
    emit('selected')
  }
}

const handleTabBack = (e: Event) => {
  emit('tab-back', e)
}

const handleTabForward = (e: Event) => {
  emit('tab-forward', e)
}

const searchChange = async (search: string): Promise<void> => {
  if (!search) {
    isLoading.value = false
    return
  }

  searchText.value = search

  try {
    const response = await fetchAdjustmentAccounts({
      ledgerId: ledgerId.value,
      searchString: encodeURIComponent(search),
    })

    if (!response) {
      return
    }

    accountSelectorInfo.value = response
  } catch (e) {
    const err = e as {
      body: string
      ok: boolean
      status: number
      statusText: string
    }

    if (err?.body === abortAutocompleteReason) {
      return
    }

    SfLog.warn('Adjustments::Error fetching autocomplete', {
      extra: {
        firmId: window.Silverfin.currentFirmId,
        companyId: window.Silverfin.currentCompanyId,
        ledgerId: ledgerId.value,
        error: e,
      },
    })
  } finally {
    isLoading.value = false
  }
}

const debouncedSearchChange = debounce(searchChange, 300, { leading: true })

function handleSearch(search: string): void {
  // Cancel any typeahead in-flight requests
  abortSearch()
  isLoading.value = true
  debouncedSearchChange(search)
}

function abortSearch(): void {
  abort('typeahead', abortAutocompleteReason)
}

onBeforeUnmount(() => {
  if (debouncedSearchChange.cancel) {
    debouncedSearchChange.cancel()
  }

  abortSearch()
})
</script>
